import BaseModel from './BaseModel';
import ArticleAttribute from './ArticleAttribute';
import Tag from './Tag';
import { Utils } from '../services/';
import * as _ from 'es-toolkit';

const as_translatable = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value))
        return value;
    if (typeof value === 'string' && value.length)
        return { [Utils.default_lang()]: value };
    return {};
};

/** Class representing an article. */
class Article extends BaseModel {
    static TYPE_PRODUCT              = 'product';
    static TYPE_MANUAL_DISCOUNT      = 'manual_discount';
    static TYPE_AUTO_DISCOUNT        = 'auto_discount';
    static TYPE_CODE_DISCOUNT        = 'code_discount';
    static DISCOUNT_TARGET_CART           = 'cart';
    static DISCOUNT_TARGET_LAST_ITEM      = 'last_item';
    static DISCOUNT_TARGET_MOST_EXPENSIVE = 'most_expensive';
    static DISCOUNT_TARGET_SKIP_FIRST     = 'skip_first';

    static discountTargets = () => {
        return [
            Article.DISCOUNT_TARGET_CART,
            Article.DISCOUNT_TARGET_LAST_ITEM,
            Article.DISCOUNT_TARGET_MOST_EXPENSIVE,
            Article.DISCOUNT_TARGET_SKIP_FIRST
        ];
    };

    constructor(properties) {
        super(properties);

        this.tags       = (this.tags || []).map(t => new Tag(t));
        this.attributes = (this.attributes || []).map((a, index) => new ArticleAttribute({ ...a, index }));
        this.features   = this.attributes.filter(a => a.isFeature());
        this.extras     = this.attributes.filter(a => a.isExtra());
        (this.variants || []).forEach((v) => {
            if (!v || typeof v !== 'object')
                return;
            const display_keys = [
                'only_for_roles',
                'only_for_tickettypes',
                'exclude_tickettypes',
                'not_before',
                'not_after',
            ];
            v.rules = v.rules && typeof v.rules === 'object' ? v.rules : {};
            display_keys.forEach((key) => {
                if (v.discount?.rules && key in v.discount.rules && !(key in v.rules))
                    v.rules[key] = v.discount.rules[key];
            });
        });
    }

    addAttribute(properties) {
        this.attributes.push(new ArticleAttribute({
            ...properties,
            index: this.attributes.length
        }));
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();
        const currency = Utils.currency();

        delete prepared.features;
        delete prepared.extras;
        delete prepared.pos_ids;

        prepared.name = as_translatable(prepared.name);
        Utils.cleanTranslatableProperty(prepared, "name");
        Utils.cleanTranslatableProperty(prepared, "short_description");

        // Ensure pos is an array of objects
        prepared.pos = (prepared.pos || []).map(p => {
            return {_id: (p._id ? p._id : p)};
        });

        // Ensure tags is an array of objects
        prepared.tags = (prepared.tags || []).map((t) => {
            return t._id ? t : {'_id': t }
        });

        // Ensure delivery infos are numbers
        prepared.delivery = prepared.delivery || {};
        ['width', 'length', 'height', 'weight'].forEach((key) => {
            prepared.delivery[key] = parseFloat(prepared.delivery[key] || 0);
        });

        prepared.variants = (prepared.variants || []).filter((v) => (
            v && typeof v === 'object' && !Array.isArray(v)
        ));

        (prepared.variants || []).forEach(v => {
            v.name = as_translatable(v.name);
            Utils.cleanTranslatableProperty(v, "name");

            ['price', 'purchasing_price'].forEach((key) => {
                v[key] = v[key] || {};
                v[key][currency] = parseFloat(v[key][currency]);
            });
            v.variable_price = !!v.variable_price;
            v.value_like_price = !!v.value_like_price;
            // When "same value as price" is on, Kronos hides the value field and
            // may leave value at 0 — sync so Engine / accounting keep a real value.
            if (v.value_like_price) {
                v.value = { ...(v.price || {}) };
            } else {
                v.value = v.value || {};
                v.value[currency] = parseFloat(v.value[currency]);
            }
            ['vat', 'stock_factor'].forEach((key) => {
                v[key] = parseFloat(v[key]);
            });
            // Engine article variants have no numeric `stock` field (ENOTSUP).
            delete v.stock;
            if (!_.isNumber(v.stock_factor) || isNaN(v.stock_factor))
                v.stock_factor = 0;
            if (v.discount?.value) {
                v.discount.value.percentage = parseFloat(v.discount.value.percentage);
                v.discount.value.amount = v.discount.value.amount || {};
                v.discount.value.amount[currency] = parseFloat(v.discount.value.amount[currency]);
            }
            if (v.discount) {
                if (v.discount.target === Article.DISCOUNT_TARGET_SKIP_FIRST
                    || v.discount.skip != null) {
                    v.discount.skip = Math.max(1, parseInt(v.discount.skip, 10) || 1);
                }
                v.discount.apply_to_tickettypes = (v.discount.apply_to_tickettypes || [])
                    .filter((row) => row && row.tickettype_id)
                    .map((row) => {
                        const next = {
                            tickettype_id: row.tickettype_id,
                            units: Math.max(1, parseInt(row.units, 10) || 1),
                        };
                        if (row.pricing_id)
                            next.pricing_id = row.pricing_id;
                        return next;
                    });
                const display_keys = [
                    'only_for_roles',
                    'only_for_tickettypes',
                    'exclude_tickettypes',
                    'not_before',
                    'not_after',
                ];
                v.rules = v.rules && typeof v.rules === 'object' ? v.rules : {};
                display_keys.forEach((key) => {
                    if (v.discount.rules && key in v.discount.rules && !(key in v.rules))
                        v.rules[key] = v.discount.rules[key];
                    if (v.discount.rules)
                        delete v.discount.rules[key];
                });
            }
            if (v.discount?.rules?.only_for_articles) {
                v.discount.rules.only_for_articles = (v.discount.rules.only_for_articles || [])
                    .filter((row) => row && row.article_id)
                    .map((row) => {
                        const next = {
                            article_id: row.article_id,
                            units: Math.max(1, parseInt(row.units, 10) || 1),
                        };
                        if (row.variant_id)
                            next.variant_id = row.variant_id;
                        return next;
                    });
            }
            if (prepared.type === Article.TYPE_CODE_DISCOUNT && v.discount?.code) {
                const code_name = {};
                code_name[Utils.default_lang() || 'fr'] = v.discount.code;
                v.name = code_name;
                if (prepared.variants.length === 1)
                    prepared.name = { ...code_name };
            }
        });

        (prepared.attributes || []).forEach((a) => {
            delete a.index;
            a.name = as_translatable(a.name);
            Utils.cleanTranslatableProperty(a, "name");

            if ('min_nb_choices' in a) {
                const min_nb_choices = parseInt(a.min_nb_choices, 10);
                a.min_nb_choices = Number.isNaN(min_nb_choices) ? 0 : min_nb_choices;
            }

            if ('max_nb_choices' in a) {
                const max_nb_choices = a.max_nb_choices;
                const is_empty = max_nb_choices === null
                    || max_nb_choices === ''
                    || (typeof max_nb_choices === 'string' && max_nb_choices.length === 0);
                if (is_empty)
                    delete a.max_nb_choices;
                else {
                    const parsed = parseInt(max_nb_choices, 10);
                    if (Number.isNaN(parsed))
                        delete a.max_nb_choices;
                    else
                        a.max_nb_choices = parsed;
                }
            }

            let hasDefault = false;
            (a.choices || []).forEach(choice => {
                choice.name = as_translatable(choice.name);
                Utils.cleanTranslatableProperty(choice, "name");
                choice.price = choice.price || {};
                choice.price[currency] = parseFloat(choice.price[currency] ?? '0');
                if (isNaN(choice.price[currency]))
                    choice.price[currency] = 0.00;
                choice.default = !hasDefault && choice.default;
                hasDefault     = hasDefault || choice.default;
            });
        });

        return prepared;
    }
}

/**
 * Get the fields to get server side when we
 * ask for a listing
 */
Article.getListingFields = () => [
    '_id',
    'name',
    'type',
    'pos',
    'posters',
    'sort_weight',
    'short_description',
    'variants',
    'tags'
];

export default Article;
